import {
  Button,
  clx,
  ControlledAutocompleteSelect,
  LabelRegular,
  Loading,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  TextRegular
} from '@hm/fundamental'
import {mdiCheck, mdiWindowClose} from '@mdi/js'
import React, {FC, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {useHierarchyNodeOptions} from '../../hooks'
import {HierarchyLevel} from '../../types'

export const MAX_COMPARISON_ITEMS = 5

interface AddHierarchyNodeDialogProps {
  onClose: () => void
  onAdd: (selectedValues: (string | number)[]) => void
  hierarchyLevel: HierarchyLevel
  initialSelectedValues?: string[]
}

export const AddHierarchyNodeDialog: FC<AddHierarchyNodeDialogProps> = ({
  onClose,
  onAdd,
  hierarchyLevel,
  initialSelectedValues = []
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data: options = [], isLoading} = useHierarchyNodeOptions(hierarchyLevel)
  const [selectedValues, setSelectedValues] = useState<(string | number)[]>(initialSelectedValues)

  const isExceedingMax = selectedValues.length > MAX_COMPARISON_ITEMS
  const isAddDisabled = selectedValues.length === 0 || isExceedingMax

  return (
    <Modal open onClose={onClose} data-cy="add-hierarchy-node-dialog">
      <ModalTitle data-cy="add-hierarchy-node-dialog-title">
        {t('comparativeAnalysis.addDialog.title')}
      </ModalTitle>
      <ModalContent>
        <TextRegular className="text-foreground_faded">
          {t('comparativeAnalysis.addDialog.description', {max: MAX_COMPARISON_ITEMS})}
        </TextRegular>
        {isLoading ? (
          <div className="flex justify-center py-4">
            <Loading size="large" />
          </div>
        ) : (
          <>
            <ControlledAutocompleteSelect
              label={t('comparativeAnalysis.addDialog.selectLabel', {
                level: t(`comparativeAnalysis.hierarchyLevel.${hierarchyLevel}`)
              })}
              options={options}
              selectedValues={selectedValues}
              onChange={setSelectedValues}
              isMultiSelect
              data-cy="hierarchy-node-select"
            />
            <LabelRegular
              className={clx('mt-1', isExceedingMax ? 'text-negative' : '')}
              data-cy="max-selection-hint"
            >
              {t('comparativeAnalysis.addDialog.maxSelectionHint', {max: MAX_COMPARISON_ITEMS})}
            </LabelRegular>
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <Button
          icon={mdiWindowClose}
          variant="secondary"
          onClick={onClose}
          data-cy="add-hierarchy-node-cancel-button"
        >
          {t('comparativeAnalysis.addDialog.cancel')}
        </Button>
        <Button
          icon={mdiCheck}
          onClick={() => onAdd(selectedValues)}
          disabled={isAddDisabled}
          data-cy="add-hierarchy-node-add-button"
        >
          {t('comparativeAnalysis.addDialog.add')}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
